import { httpClient } from "../../../lib/http/axios";
import type { AuthSession, AuthUser } from "../types";
import {
  type AuthLegacyJumpUrlSuccessResponse,
  type AuthPasswordResetSuccessResponse,
  type AuthLoginRequest,
  type AuthRegisterRequest,
  type AuthSessionSuccessResponse,
  type AuthUserSuccessResponse,
  type WechatBindRequest,
  type WechatLoginRequest,
  mapAuthLegacyJumpUrl,
  mapAuthLegacyJumpUrlPayload,
  mapAuthLegacySingleLoginPayload,
  mapAuthPasswordResetPayload,
  mapAuthSession,
  mapAuthUser,
} from "../api/adapters";
import type {
  AuthLegacyJumpUrlMutation,
  AuthLegacyJumpUrlPayload,
  AuthPasswordResetMutation,
  AuthPasswordResetPayload,
} from "../types";

/** 使用账号密码登录。 */
export async function loginAccount(payload: AuthLoginRequest): Promise<AuthSession> {
  const response = await httpClient.post<AuthSessionSuccessResponse>("/auth/login", payload);
  return mapAuthSession(response.data.data);
}

/** 使用旧系统单点号登录新系统账号。 */
export async function loginWithLegacySingleLogin(singleLoginNo: string): Promise<AuthSession> {
  const response = await httpClient.post<AuthSessionSuccessResponse>(
    "/auth/legacy-single-login",
    mapAuthLegacySingleLoginPayload(singleLoginNo),
  );
  return mapAuthSession(response.data.data);
}

/** 生成当前用户进入旧系统的 JumpLoginDispose 地址。 */
export async function buildLegacyJumpUrl(
  payload: AuthLegacyJumpUrlMutation,
): Promise<AuthLegacyJumpUrlPayload> {
  const response = await httpClient.post<AuthLegacyJumpUrlSuccessResponse>(
    "/auth/legacy-jump-url",
    mapAuthLegacyJumpUrlPayload(payload),
  );
  return mapAuthLegacyJumpUrl(response.data.data);
}

/** 注册 sys_user 账号，注册后默认启用但无角色。 */
export async function registerAccount(payload: AuthRegisterRequest): Promise<AuthSession> {
  const response = await httpClient.post<AuthSessionSuccessResponse>("/auth/register", payload);
  return mapAuthSession(response.data.data);
}

/** 获取当前登录用户资料。 */
export async function fetchCurrentAuthUser(): Promise<AuthUser> {
  const response = await httpClient.get<AuthUserSuccessResponse>("/auth/me");
  return mapAuthUser(response.data.data);
}

/** 管理员重置指定登录用户密码。 */
export async function resetCurrentAuthUserPassword(
  userId: number,
  payload: AuthPasswordResetMutation,
): Promise<AuthPasswordResetPayload> {
  const response = await httpClient.put<AuthPasswordResetSuccessResponse>(
    `/auth/admin/users/${userId}/password`,
    mapAuthPasswordResetPayload(payload),
  );
  return {
    userId: response.data.data.user_id,
  };
}

/** 使用微信公众号授权 code 登录。 */
export async function loginWithWechatCode(code: string): Promise<AuthSession> {
  const payload: WechatLoginRequest = { code };
  const response = await httpClient.post<AuthSessionSuccessResponse>("/auth/wechat/login", payload);
  return mapAuthSession(response.data.data);
}

/** 绑定当前账号的微信公众号身份。 */
export async function bindWechatIdentity(code: string): Promise<AuthUser> {
  const payload: WechatBindRequest = { code };
  const response = await httpClient.post<AuthUserSuccessResponse>("/auth/wechat/bind", payload);
  return mapAuthUser(response.data.data);
}
